// TeamTasksPanel — Storico task completati filtrabile + Report Produttività settimanale
import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { CheckCircle2, Loader2, TrendingUp, Users as UsersIcon, Filter, Download } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "../utils/api";
import { etichettaTask } from "../utils/etichettaTask";

const TASK_TYPE_LABELS = {
  call_strategica: "Call Strategica",
  script_videomaker: "Script Videomaker",
  script_cliente: "Script Cliente",
  conferma_script: "Conferma Script",
  ped_programmazione: "PED + Programmazione",
  avvio_adv: "Avvio ADV",
  appuntamento_successivo: "Appuntamento Successivo",
  altro: "Altro",
};

const roleLabel = (it) => etichettaTask(it, TASK_TYPE_LABELS) || "-";

// Default periodo: ultimi 30 giorni
const isoDaysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

export default function TeamTasksPanel() {
  const [from, setFrom] = useState(isoDaysAgo(29));
  const [to, setTo] = useState(isoDaysAgo(0));
  const [operator, setOperator] = useState("all");
  const [client, setClient] = useState("all");
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [history, setHistory] = useState(null);
  const [productivity, setProductivity] = useState(null);
  const [loading, setLoading] = useState(false);

  // Carica lista operativi + clienti per i filtri
  useEffect(() => {
    (async () => {
      try {
        const [uRes, cRes] = await Promise.all([
          apiFetch("/users"),
          apiFetch("/clients"),
        ]);
        if (uRes.ok) {
          const data = await uRes.json();
          setUsers((data.users || data || []).map((u) => ({ id: u.user_id, name: u.name || u.email })));
        }
        if (cRes.ok) {
          const data = await cRes.json();
          setClients((data || []).map((c) => ({ id: c.client_id, name: c.name })));
        }
      } catch (e) { /* silent */ }
    })();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ from, to, operator, client });
      const [h, p] = await Promise.all([
        apiFetch(`/task-stats/completed?${params.toString()}`),
        apiFetch(`/task-stats/productivity?from=${from}&to=${to}`),
      ]);
      if (h.ok) setHistory(await h.json());
      else toast.error("Errore storico task");
      if (p.ok) setProductivity(await p.json());
    } catch (e) {
      toast.error("Errore: " + (e.message || ""));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [from, to, operator, client]);

  const exportCsv = () => {
    if (!history?.items?.length) return;
    const rows = [["Data completamento", "Operativo", "Cliente", "Tipo task", "Titolo"]];
    history.items.forEach((it) => {
      rows.push([
        it.completed_at || "",
        it.operator_name || "",
        it.client_name || "",
        roleLabel(it),
        (it.title || "").replace(/[\r\n]+/g, " "),
      ]);
    });
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `storico-task_${from}_${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const topOperatorsForCharts = useMemo(() => {
    if (!productivity?.totals_by_operator?.length) return [];
    const max = Math.max(...productivity.totals_by_operator.map((o) => o.count), 1);
    return productivity.totals_by_operator.slice(0, 10).map((o) => ({ ...o, pct: (o.count / max) * 100 }));
  }, [productivity]);

  return (
    <div className="space-y-4">
      {/* Filtri */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3 text-sm text-muted-foreground">
            <Filter className="w-4 h-4" />
            <span>Filtri storico</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Da</Label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="team-tasks-from" />
            </div>
            <div>
              <Label className="text-xs">A</Label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="team-tasks-to" />
            </div>
            <div>
              <Label className="text-xs">Operativo</Label>
              <Select value={operator} onValueChange={setOperator}>
                <SelectTrigger data-testid="team-tasks-operator"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  {users.map((u) => (<SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Cliente</Label>
              <Select value={client} onValueChange={setClient}>
                <SelectTrigger data-testid="team-tasks-client"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  {clients.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading && (
        <div className="text-center py-6 text-muted-foreground">
          <Loader2 className="w-4 h-4 inline mr-2 animate-spin" /> Caricamento…
        </div>
      )}

      {/* Report Produttività settimanale */}
      {productivity && (
        <Card className="border-emerald-200 bg-emerald-50/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-700" />
              Report Produttività Team
              <span className="text-xs font-normal text-slate-500 ml-auto">
                {productivity.total_completed} task chiusi · {productivity.weeks.length} settimane
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Totali per operativo (top 10) */}
            {topOperatorsForCharts.length > 0 ? (
              <div className="space-y-2">
                <div className="text-xs uppercase font-semibold text-slate-600 tracking-wide flex items-center gap-2">
                  <UsersIcon className="w-3.5 h-3.5" /> Classifica operatori (periodo intero)
                </div>
                {topOperatorsForCharts.map((op) => (
                  <div key={op.user_id} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 text-sm" data-testid={`prod-op-${op.user_id}`}>
                    <div className="w-full sm:w-40 truncate font-medium">{op.name}</div>
                    <div className="flex-1 h-3 bg-white rounded-full border overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600" style={{ width: `${op.pct}%` }} />
                    </div>
                    <div className="text-right font-mono tabular-nums font-bold text-emerald-700 w-16">{op.count}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-slate-500 text-center py-3">Nessun task chiuso nel periodo.</div>
            )}

            {/* Breakdown per settimana */}
            {productivity.weeks.length > 0 && (
              <div className="space-y-2 pt-3 border-t border-emerald-200">
                <div className="text-xs uppercase font-semibold text-slate-600 tracking-wide">Dettaglio per settimana</div>
                <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
                  {productivity.weeks.map((w) => (
                    <div key={w.week} className="bg-white rounded-md border p-2">
                      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                        <Badge variant="outline" className="text-[10px]">{w.week}</Badge>
                        <span className="text-xs text-slate-500">{w.total} task chiusi</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {w.operators.map((op) => (
                          <span key={op.user_id} className="text-[11px] bg-slate-100 border rounded-full px-2 py-0.5">
                            <b>{op.name}</b>: {op.count}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Storico task */}
      {history && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 flex-wrap">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              Storico Task Completati
              <span className="text-xs font-normal text-slate-500">
                {history.count} task · {history.from} → {history.to}
              </span>
              <Button size="sm" variant="outline" onClick={exportCsv} disabled={!history.items?.length} className="ml-auto h-8" data-testid="team-tasks-export">
                <Download className="w-3.5 h-3.5 mr-1" /> CSV
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {history.items.length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-6">Nessun task completato con i filtri applicati.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b text-xs uppercase text-slate-500">
                      <th className="py-2 px-2">Data</th>
                      <th className="py-2 px-2">Operativo</th>
                      <th className="py-2 px-2">Cliente</th>
                      <th className="py-2 px-2 hidden sm:table-cell">Tipo</th>
                      <th className="py-2 px-2 hidden md:table-cell">Titolo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.items.map((it) => (
                      <tr key={it.task_id} className="border-b hover:bg-slate-50" data-testid={`task-row-${it.task_id}`}>
                        <td className="py-2 px-2 whitespace-nowrap text-xs">
                          {it.completed_at ? new Date(it.completed_at).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }) : "-"}
                        </td>
                        <td className="py-2 px-2 font-medium">{it.operator_name}</td>
                        <td className="py-2 px-2 text-slate-700">{it.client_name}</td>
                        <td className="py-2 px-2 hidden sm:table-cell text-xs text-slate-600">{roleLabel(it)}</td>
                        <td className="py-2 px-2 hidden md:table-cell text-xs text-slate-600 truncate max-w-xs">{it.title}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
